// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@keller/App/Components/Notify/Notify";
import { color, ellipsis, xScreen } from "@keller/XDL/Utilities";
import { getSystemColor } from "@keller/XDL/Utilities/color";
import { iconButtonCommonStyles } from "@keller/XDL/Utilities/links";
import { mergeWithCss } from "@keller/XDL/Utilities/mergeWithCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    cartLine: CartLineModel;
    errorMessages?: React.ReactNode[];
    cart?: Readonly<Cart>;
    qtyOrderedChangeHandler: (cartLine: CartLineModel, qtyOrdered: number) => void;
    removeCartLineClickHandler: (cartLine: CartLineModel) => void;
}

interface MiniCartLineStyles {
    cartLine?: InjectableCss;
    imageWrapper?: InjectableCss;
    cartLineImage?: LazyImageProps;
    detailsWrapper?: InjectableCss;
    partNumbersStyles?: ProductPartNumbersStyles;
    brand?: LinkPresentationProps;
    lineDescription?: LinkPresentationProps;
    productInfoWrapper?: InjectableCss;
    qtyAndPriceWrapper?: InjectableCss;
    qtyAndRemoveWrapper?: InjectableCss;
    cartLineQuantityStyles?: CartLineQuantityStyles;
    extendedPriceWrapper?: InjectableCss;
    basicProductPriceStyles?: ProductPriceStyles;
    extendedNetPriceValue?: TypographyProps;
    removeCartLineLink?: LinkPresentationProps;
    errorMessagesWrapper?: InjectableCss;
    removeConfirmation?: InjectableCss;
    removeConfirmationText?: TypographyProps;
    removeConfirmationConfirm?: ButtonPresentationProps;
    removeConfirmationClose?: ButtonPresentationProps;
}

const miniCartLineStyles: MiniCartLineStyles = {
    cartLine: {
        css: css`
            display: flex;
            position: relative;
            padding-left: 20px;
            padding-right: 20px;
            margin-bottom: 30px;
            ${xScreen("lte", "md")} {
                border-bottom: 1px solid ${color("common.border")};
                padding-right: 0;
                padding-bottom: 15px;
                flex: 0 1 calc(50% - 10px);
                width: 100%;
                margin-bottom: 0;
            }
            ${xScreen("lte", 560)} {
                border-bottom: 0;
                padding-bottom: 0;
                flex: 1;
            }
        `,
    },
    imageWrapper: {
        css: css`
            width: 72px;
            height: 72px;
            overflow: hidden;
            margin-right: 16px;
            flex-shrink: 0;
        `,
    },
    cartLineImage: {
        width: "72px",
        height: "72px",
        css: css`
            max-height: 100%;
            max-width: 100%;
            img {
                height: auto;
            }
        `,
    },
    detailsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1;
            ${xScreen("lte", "md")} {
                width: 100%;
                max-width: initial;
            }
        `,
    },
    productInfoWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            margin-bottom: 12px;
        `,
    },
    partNumbersStyles: {
        container: {
            css: css`
                margin-right: 5px;
                width: auto;
                flex: 1 0 auto;
                min-width: 174px;
                max-width: 100%;
            `,
        },
        erpNumberLabelText: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                &:after {
                    content: ":";
                }
            `,
        },
        erpNumberValueText: {
            size: 12,
            weight: "normal",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
            `,
        },
    },
    brand: {
        typographyProps: {
            size: 12,
            weight: "normal",
            css: css`
                letter-spacing: 0.5px;
                line-height: 13px;
                ${ellipsis(1)}
            `,
        },
        css: css`
            margin-right: 10px;
        `,
    },
    lineDescription: {
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                ${ellipsis(2)}
            `,
        },
        css: css`
            margin-bottom: 5px;
            max-width: 90%;
        `,
    },
    qtyAndPriceWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            justify-content: space-between;
        `,
    },
    qtyAndRemoveWrapper: {
        css: css`
            margin-right: 5px;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
        `,
    },
    cartLineQuantityStyles: {
        editableQuantityTextField: {
            sizeVariant: "small",
            labelProps: {
                css: css`
                    display: none;
                `,
            },
            cssOverrides: {
                formField: css`
                    width: auto;
                `,
                inputSelect: css`
                    width: 74px;
                    text-align: center;
                    font-size: 13px;
                    font-weight: 600;
                    letter-spacing: 0;
                    line-height: 18px;
                `,
            },
        },
    },
    extendedPriceWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    basicProductPriceStyles: {
        price: {
            priceText: {
                size: 18,
                weight: 900,
                css: css`
                    letter-spacing: 0;
                    line-height: 18px;
                    margin-right: 4px;
                `,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    weight: "normal",
                    size: 12,
                    css: css`
                        letter-spacing: 0;
                        line-height: 18px;
                    `,
                },
            },
        },
    },
    removeCartLineLink: {
        icon: {
            iconProps: {
                src: "X",
                size: 14,
                color: "secondary",
                css: css`
                    margin-right: 5px;
                `,
            },
        },
        css: css`
            margin-left: auto;
            position: absolute;
            right: 0;
        `,
    },
    errorMessagesWrapper: {
        css: css`
            width: 100%;
            margin-top: 10px;
            & > div:not(:last-child) {
                margin-bottom: 15px;
            }
        `,
    },
    removeConfirmation: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            height: 0;
            margin-bottom: 0;
            transition: all 0.3s ease-in-out;
            overflow: hidden;
            opacity: 0;
            &[aria-expanded="true"] {
                height: 38px;
                border-top: 1px solid ${color("common.ui2")};
                // remove padding-bottom: 15px from parent wrapper
                margin-top: 10px;
                margin-bottom: -14px;
                opacity: 1;
            }
        `,
    },
    removeConfirmationText: {
        size: 12,
        color: "text.main",
    },
    removeConfirmationConfirm: {
        ...iconButtonCommonStyles(Check, 10, {
            typographyProps: {
                size: 13,
                color: getSystemColor("text.main", 0.6),
            },
        }),
    },
    removeConfirmationClose: {
        ...mergeWithCss(
            iconButtonCommonStyles(X, 10, {
                typographyProps: {
                    size: 13,
                    color: getSystemColor("text.main", 0.6),
                },
            }),
            {
                css: css`
                    ${IconWrapper} {
                        &,
                        & svg {
                            width: 9px;
                            height: 12px;
                        }
                    }
                `,
            },
        ),
    },
};

const styles = miniCartLineStyles;

type Props = OwnProps;

const MiniCartLineCard: FC<Props> = ({
    cartLine,
    errorMessages,
    cart,
    qtyOrderedChangeHandler,
    removeCartLineClickHandler,
}) => {
    const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);

    return (
        <CartLineContext.Provider value={cartLine} data-test-selector={`miniCartLine_${cartLine.id}`}>
            <StyledWrapper {...styles.cartLine} data-test-selector="cartLine">
                <StyledWrapper {...styles.imageWrapper}>
                    <Link href={cartLine.productUri}>
                        <LazyImage src={cartLine.smallImagePath} altText={cartLine.altText} {...styles.cartLineImage} />
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.detailsWrapper}>
                    <StyledWrapper
                        css={css`
                            display: flex;
                            justify-content: space-between;
                            position: relative;
                        `}
                    >
                        {cartLine.brand && (
                            <Link href={cartLine.brand.detailPagePath} {...styles.brand}>
                                {cartLine.brand.name}
                            </Link>
                        )}
                        {!showRemoveConfirmation && (
                            <Link
                                onClick={() => {
                                    setShowRemoveConfirmation(true);
                                }}
                                data-test-selector="cartline_removeLine"
                                {...styles.removeCartLineLink}
                            />
                        )}
                    </StyledWrapper>
                    <StyledWrapper>
                        <Link href={cartLine.productUri} {...styles.lineDescription}>
                            {cartLine.shortDescription}
                        </Link>
                    </StyledWrapper>
                    <StyledWrapper {...styles.productInfoWrapper}>
                        <ProductPartNumbers
                            manufacturerItem={cartLine.manufacturerItem}
                            productNumber={cartLine.erpNumber}
                            extendedStyles={styles.partNumbersStyles}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.qtyAndPriceWrapper}>
                        <StyledWrapper {...styles.qtyAndRemoveWrapper}>
                            <CartLineQuantity
                                cart={cart as Cart}
                                editable={true}
                                extendedStyles={styles.cartLineQuantityStyles}
                                onQtyOrderedChange={(inputQty: number) => {
                                    qtyOrderedChangeHandler && qtyOrderedChangeHandler(cartLine, inputQty);
                                }}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.extendedPriceWrapper}>
                            {!cart?.cartNotPriced && (
                                <ProductPrice
                                    showLabel={false}
                                    showSavings={false}
                                    product={cartLine}
                                    extendedStyles={styles.basicProductPriceStyles}
                                />
                            )}
                        </StyledWrapper>
                        <StyledWrapper
                            {...({ showRemoveConfirmation } as any)}
                            {...styles.removeConfirmation}
                            aria-expanded={showRemoveConfirmation}
                        >
                            <Typography {...styles.removeConfirmationText}>{translate("Are You Sure?")}</Typography>
                            <Button
                                onClick={() => {
                                    removeCartLineClickHandler(cartLine);
                                }}
                                {...styles.removeConfirmationConfirm}
                            >
                                {translate("Yes, Remove")}
                            </Button>
                            <Button
                                onClick={() => {
                                    setShowRemoveConfirmation(false);
                                }}
                                {...styles.removeConfirmationClose}
                            >
                                {translate("No")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                    {errorMessages && (
                        <StyledWrapper {...styles.errorMessagesWrapper}>
                            {errorMessages.map(message => (
                                <Notify
                                    type="danger"
                                    data-test-selector="cartline_errorMessage"
                                    key={`error_${cartLine.id}`}
                                >
                                    {message}
                                </Notify>
                            ))}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </CartLineContext.Provider>
    );
};

export default MiniCartLineCard;
